import React, { useState, useEffect } from 'react';
import { ChevronDown, Loader2, Activity, Thermometer, Droplets, Droplet, Wind, Scale, Heart } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { Skeleton } from '../../../skeletons';
import residencesService from '../../../services/carehome_manager/residencesService';

// Per-vital chip styling for the type selector. Written as full literal class
// strings (not `bg-${color}-50`) so Tailwind actually generates them, including
// the dark-mode variants — dynamic template classes get purged from the build.
const VITAL_CHIP = {
  blue:   { active: 'bg-blue-50 border-blue-200 text-blue-600 dark:bg-blue-900/25 dark:border-blue-800/60 dark:text-blue-300',       icon: 'text-blue-600 dark:text-blue-300' },
  red:    { active: 'bg-red-50 border-red-200 text-red-600 dark:bg-red-900/25 dark:border-red-800/60 dark:text-red-300',           icon: 'text-red-600 dark:text-red-300' },
  orange: { active: 'bg-orange-50 border-orange-200 text-orange-600 dark:bg-orange-900/25 dark:border-orange-800/60 dark:text-orange-300', icon: 'text-orange-600 dark:text-orange-300' },
  purple: { active: 'bg-purple-50 border-purple-200 text-purple-600 dark:bg-purple-900/25 dark:border-purple-800/60 dark:text-purple-300', icon: 'text-purple-600 dark:text-purple-300' },
  green:  { active: 'bg-green-50 border-green-200 text-green-600 dark:bg-green-900/25 dark:border-green-800/60 dark:text-green-300',    icon: 'text-green-600 dark:text-green-300' },
  cyan:   { active: 'bg-cyan-50 border-cyan-200 text-cyan-600 dark:bg-cyan-900/25 dark:border-cyan-800/60 dark:text-cyan-300',       icon: 'text-cyan-600 dark:text-cyan-300' },
  teal:   { active: 'bg-teal-50 border-teal-200 text-teal-600 dark:bg-teal-900/25 dark:border-teal-800/60 dark:text-teal-300',       icon: 'text-teal-600 dark:text-teal-300' },
};

// `fixedType` locks the tab to a single vital (e.g. "Weight") and hides the
// type selector — used to render Weight as its own tab instead of one of the
// vitals options.
const VitalsTab = ({ fixedType = null }) => {
    const { id } = useParams();
    const [vitalsType, setVitalsType] = useState(fixedType || 'Blood Pressure');
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({
        records: [],
        chartData: []
    });

    // The same six vitals, in the same order and under the same names, as the
    // app's vitals cards and the care-note vitals action. These labels are sent
    // to the API as `vital_type` — see api/data/vitalTypes.js, which is the one
    // list all three surfaces spell from. Weight sits at the end because it is
    // rendered as its own tab via `fixedType`, not as a chip.
    const vitalTypes = [
        { label: 'Temperature', icon: Thermometer, color: 'orange' },
        { label: 'Heart Rate', icon: Heart, color: 'red' },
        { label: 'Respiratory', icon: Wind, color: 'cyan' },
        { label: 'Blood Pressure', icon: Activity, color: 'blue' },
        { label: 'Blood Sugar', icon: Droplets, color: 'purple' },
        { label: 'Blood Oxygen', icon: Droplet, color: 'teal' },
        { label: 'Weight', icon: Scale, color: 'green' },
    ];

    // Keep the selected type in sync if the locked `fixedType` prop changes
    // while this instance stays mounted (e.g. reused across sub-tab switches).
    useEffect(() => {
        if (fixedType) setVitalsType(fixedType);
    }, [fixedType]);

    useEffect(() => {
        const fetchVitals = async () => {
            try {
                setLoading(true);
                const response = await residencesService.getResidentVitals(id, vitalsType);
                setData({ records: [], chartData: [], ...response.data });
            } catch (err) {
                console.error('Error fetching vitals:', err);
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchVitals();
        }
    }, [id, vitalsType]);

    /**
     * A day only counts as plotted when there is a reading for it. The API sends
     * null for days with nothing recorded (older builds sent 0, which is not a
     * temperature anyone has), so both are treated as gaps — otherwise the line
     * ran along the bottom of the chart and shot vertically up to the single
     * real reading.
     */
    const isReading = (value) => typeof value === 'number' && Number.isFinite(value) && value > 0;

    /**
     * The y-axis band for a vital. Starting every axis at 0 is what made a
     * normal 36.8°C sit at the very top of a 0–42 chart: the interesting range
     * is narrow and nowhere near zero. Each vital gets its clinical band, then
     * the band is widened if a reading falls outside it so nothing is clipped.
     */
    const AXIS_BANDS = {
        'Blood Pressure': { min: 40, max: 200 },
        'Heart Rate': { min: 40, max: 160 },
        'Temperature': { min: 34, max: 42 },
        'Blood Sugar': { min: 2, max: 20 },
        'Respiratory': { min: 8, max: 30 },
        'Blood Oxygen': { min: 85, max: 100 },
    };

    const axisBand = (values) => {
        const band = AXIS_BANDS[vitalsType];
        if (!values.length) return band || { min: 0, max: 150 };

        const lo = Math.min(...values);
        const hi = Math.max(...values);

        if (!band) {
            // Weight and anything unlisted: frame the data with a little padding.
            const pad = Math.max(2, (hi - lo) * 0.25 || hi * 0.1);
            return { min: Math.max(0, Math.floor(lo - pad)), max: Math.ceil(hi + pad) };
        }

        return {
            min: Math.min(band.min, Math.floor(lo - 1)),
            max: Math.max(band.max, Math.ceil(hi + 1)),
        };
    };

    const VitalsChart = () => {
        const points = data.chartData
            .map((d, i) => ({ ...d, index: i }))
            .filter((d) => isReading(d.value));

        const { min, max } = axisBand(points.map((p) => p.value));
        const span = max - min || 1;

        const stepX = data.chartData.length > 1 ? 1000 / (data.chartData.length - 1) : 0;
        const xOf = (index) => index * stepX;
        const yOf = (value) => 200 - ((value - min) / span) * 200;

        // One line through the readings, skipping the days without one.
        const path = points.length
            ? points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xOf(p.index)},${yOf(p.value)}`).join(' ')
            : '';
        // The fill closes down to the baseline under the first and last reading
        // only, so empty days are not shaded as if they held a value.
        const areaPath = points.length > 1
            ? `${path} L ${xOf(points[points.length - 1].index)},200 L ${xOf(points[0].index)},200 Z`
            : '';

        const activeColor = vitalTypes.find(t => t.label === vitalsType)?.color || 'blue';
        const colorHex = {
            blue: '#3b82f6',
            red: '#ef4444',
            orange: '#f97316',
            purple: '#a855f7',
            green: '#22c55e',
            cyan: '#06b6d4',
            teal: '#14b8a6'
        }[activeColor];

        const axisLabel = (fraction) => {
            const value = min + span * fraction;
            // Temperature and blood sugar need a decimal to be useful.
            return span <= 20 ? value.toFixed(1) : Math.round(value);
        };

        return (
            <div className="w-full">
                <div className="flex items-center justify-between mb-8">
                    <h3 className="text-[16px] md:text-[18px] font-medium text-[#1E293B] dark:text-gray-100 tracking-tight">{vitalsType} Chart</h3>
                    <div className="flex gap-3">
                        <button className="flex items-center gap-2 px-3 py-1.5 border border-gray-200 dark:border-gray-800 rounded-md text-[12px] md:text-[13px] font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#31373f] transition-colors">
                            Last 7 Days
                        </button>
                    </div>
                </div>

                <div className="flex">
                    <div className="w-[45px] flex flex-col justify-between text-[11px] font-medium text-[#1E293B] dark:text-gray-400 pb-[30px] pr-2 h-[200px] text-right">
                        <span>{axisLabel(1)}</span>
                        <span>{axisLabel(0.75)}</span>
                        <span>{axisLabel(0.5)}</span>
                        <span>{axisLabel(0.25)}</span>
                        <span>{axisLabel(0)}</span>
                    </div>

                    <div className="flex-1 relative h-[200px]">
                        <svg viewBox="0 0 1000 200" className="w-full h-full overflow-visible" preserveAspectRatio="none">
                            <defs>
                                <linearGradient id="gradVitals" x1="0%" y1="0%" x2="0%" y2="100%">
                                    <stop offset="0%" stopColor={colorHex} stopOpacity="0.4" />
                                    <stop offset="100%" stopColor={colorHex} stopOpacity="0" />
                                </linearGradient>
                            </defs>
                            <line x1="0" y1="0" x2="1000" y2="0" stroke="#f8fafc" strokeWidth="1.5" className="dark:stroke-gray-800" />
                            <line x1="0" y1="50" x2="1000" y2="50" stroke="#f8fafc" strokeWidth="1.5" className="dark:stroke-gray-800" />
                            <line x1="0" y1="100" x2="1000" y2="100" stroke="#f8fafc" strokeWidth="1.5" className="dark:stroke-gray-800" />
                            <line x1="0" y1="150" x2="1000" y2="150" stroke="#f8fafc" strokeWidth="1.5" className="dark:stroke-gray-800" />
                            <line x1="0" y1="200" x2="1000" y2="200" stroke="#f8fafc" strokeWidth="1.5" className="dark:stroke-gray-800" />

                            {areaPath && <path d={areaPath} fill="url(#gradVitals)" />}
                            {path && <path d={path} fill="none" stroke={colorHex} strokeWidth="3" />}
                            {points.map((p) => (
                                <circle
                                    key={p.index}
                                    cx={xOf(p.index)}
                                    cy={yOf(p.value)}
                                    r="4"
                                    fill={colorHex}
                                    stroke="white"
                                    strokeWidth="2"
                                />
                            ))}
                        </svg>
                        {!points.length && (
                            <div className="absolute inset-0 flex items-center justify-center text-[13px] font-medium text-gray-400 dark:text-gray-500">
                                No {vitalsType.toLowerCase()} readings in the last 7 days
                            </div>
                        )}
                        <div className="absolute -bottom-8 w-full flex justify-between text-[11px] font-medium text-[#1E293B] dark:text-gray-400">
                            {data.chartData.map(d => (
                                <span key={d.date}>{new Date(d.date).toLocaleDateString('en-GB', { weekday: 'short' })}</span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="w-full pt-2 animate-in fade-in duration-300">
            {/* Type Selector — hidden when locked to a single type (e.g. Weight).
                Weight is excluded here because it now lives in its own tab. */}
            {!fixedType && (
                <div className="flex flex-wrap gap-3 mb-8">
                    {vitalTypes.filter((type) => type.label !== 'Weight').map((type) => {
                        const Icon = type.icon;
                        const isActive = vitalsType === type.label;
                        const chip = VITAL_CHIP[type.color] || VITAL_CHIP.blue;
                        return (
                            <button
                                key={type.label}
                                onClick={() => setVitalsType(type.label)}
                                className={`flex items-center gap-2 px-4 py-2 rounded-xl border transition shadow-sm ${
                                    isActive
                                    ? chip.active
                                    : 'bg-white dark:bg-[#262b33] border-gray-100 dark:border-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#31373f]'
                                }`}
                            >
                                <Icon className={`w-4 h-4 ${isActive ? chip.icon : 'text-gray-400 dark:text-gray-500'}`} />
                                <span className="text-[13px] font-semibold">{type.label}</span>
                            </button>
                        );
                    })}
                </div>
            )}

            <h2 className="text-[20px] font-semibold text-[#1E293B] dark:text-gray-100 mb-6">{vitalsType} Record</h2>

            {/* Chart Section */}
            <div className="bg-white dark:bg-[#262b33] rounded-xl shadow-[0_2px_8px_-4px_rgba(0,0,0,0.05)] p-6 md:p-8 pb-12 mb-8 border border-gray-50 dark:border-gray-800">
                {loading ? (
                    <Skeleton className="h-[200px] w-full rounded-xl" />
                ) : (
                    <VitalsChart />
                )}
            </div>

            {/* Records Section */}
            <div>
                <div className="flex items-center justify-between mb-4 px-1">
                    <h3 className="text-[17px] font-semibold text-[#1E293B] dark:text-gray-100">Recent {vitalsType} Records</h3>
                    <button className="text-[13px] font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors">See all</button>
                </div>

                {/* Table Header */}
                <div className="grid grid-cols-5 px-6 py-2 text-[13px] font-medium text-gray-500 dark:text-gray-400 mb-2">
                    <div>Staff name</div>
                    <div>Date</div>
                    <div>Time</div>
                    <div>Value</div>
                    <div>Status</div>
                </div>

                {/* Table Rows */}
                <div className="flex flex-col gap-3">
                    {loading ? (
                        [1, 2, 3].map(i => (
                            <div key={i} className="h-16 bg-gray-50 dark:bg-[#262b33] animate-pulse rounded-xl border border-gray-50 dark:border-gray-800" />
                        ))
                    ) : data.records.length > 0 ? (
                        data.records.map((row) => (
                            <div
                                key={row.id}
                                className="grid grid-cols-5 items-center px-6 py-4 bg-white dark:bg-[#262b33] rounded-xl shadow-[0_2px_8px_-4px_rgba(0,0,0,0.05)] border border-gray-50 dark:border-gray-800"
                            >
                                <div className="text-[14px] font-medium text-[#1E293B] dark:text-gray-200">{row.staff}</div>
                                <div className="text-[14px] font-medium text-[#475569] dark:text-gray-400">{row.date}</div>
                                <div className="text-[14px] font-medium text-[#475569] dark:text-gray-400">{row.time}</div>
                                <div className="text-[14px] font-bold text-[#1E293B] dark:text-gray-200">
                                    {row.value} <span className="text-[11px] font-normal text-gray-400 dark:text-gray-500">{row.unit}</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                    <div className="text-[14px] font-medium text-green-600">{row.risk}</div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="py-12 text-center text-gray-500 dark:text-gray-400 bg-white dark:bg-[#262b33] rounded-xl border border-dashed border-gray-200 dark:border-gray-800">
                            No {vitalsType.toLowerCase()} records found for this period.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VitalsTab;